import React, { useEffect, useState, createContext, useContext } from 'react';
import { translations, Language, TranslationKey } from '../utils/translations';
type Theme = 'dark' | 'light' | 'neon';
interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  language: Language;
  setLanguage: (language: Language) => void;
  t: TranslationKey;
  background: string;
  setBackground: (background: string) => void;
  reduceMotion: boolean;
  setReduceMotion: (reduce: boolean) => void;
  highContrast: boolean;
  setHighContrast: (contrast: boolean) => void;
  getThemeClasses: () => {
    bg: string;
    text: string;
    card: string;
    border: string;
    accent: string;
    accentHover: string;
    cardHover: string;
    tooltip: string;
    dock: string;
    icon: string;
  };
}
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);
export function ThemeProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [theme, setTheme] = useState<Theme>(() => {
    const saved = localStorage.getItem('theme');
    return saved as Theme || 'dark';
  });
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    return saved as Language || 'nl';
  });
  const [background, setBackground] = useState(() => {
    const saved = localStorage.getItem('background');
    return saved || 'from-slate-900 via-purple-900 to-slate-900';
  });
  const [reduceMotion, setReduceMotion] = useState(() => {
    const saved = localStorage.getItem('reduceMotion');
    return saved === 'true';
  });
  const [highContrast, setHighContrast] = useState(() => {
    const saved = localStorage.getItem('highContrast');
    return saved === 'true';
  });
  useEffect(() => {
    localStorage.setItem('theme', theme);
  }, [theme]);
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);
  useEffect(() => {
    localStorage.setItem('background', background);
  }, [background]);
  useEffect(() => {
    localStorage.setItem('reduceMotion', String(reduceMotion));
  }, [reduceMotion]);
  useEffect(() => {
    localStorage.setItem('highContrast', String(highContrast));
  }, [highContrast]);
  const getThemeClasses = () => {
    if (theme === 'light') {
      return {
        bg: highContrast ? 'bg-gray-50' : 'bg-white',
        text: 'text-gray-900',
        card: highContrast ? 'bg-white' : 'bg-white/80 backdrop-blur-md',
        border: highContrast ? 'border-gray-900' : 'border-gray-200',
        accent: 'bg-purple-600 text-white',
        accentHover: 'hover:bg-purple-700',
        cardHover: 'hover:bg-gray-100',
        tooltip: 'bg-gray-900 text-white',
        dock: highContrast ? 'bg-white' : 'bg-white/90 backdrop-blur-xl',
        icon: 'text-gray-700'
      };
    }
    if (theme === 'neon') {
      return {
        bg: 'bg-black',
        text: 'text-cyan-50',
        card: highContrast ? 'bg-gray-900' : 'bg-gray-900/80 backdrop-blur-md',
        border: highContrast ? 'border-cyan-400' : 'border-cyan-500/30',
        accent: 'bg-gradient-to-r from-cyan-500 to-pink-500 text-white',
        accentHover: 'hover:from-cyan-600 hover:to-pink-600',
        cardHover: 'hover:bg-gray-800/80',
        tooltip: 'bg-cyan-500 text-black',
        dock: highContrast ? 'bg-gray-900' : 'bg-gray-900/90 backdrop-blur-xl',
        icon: 'text-cyan-400'
      };
    }
    // Dark theme (default)
    return {
      bg: `bg-gradient-to-br ${background}`,
      text: 'text-white',
      card: highContrast ? 'bg-gray-900' : 'bg-gray-900/80 backdrop-blur-md',
      border: highContrast ? 'border-white' : 'border-white/10',
      accent: 'bg-gradient-to-r from-purple-500 to-pink-500 text-white',
      accentHover: 'hover:from-purple-600 hover:to-pink-600',
      cardHover: 'hover:bg-gray-800/80',
      tooltip: 'bg-gray-800 text-white',
      dock: highContrast ? 'bg-gray-900' : 'bg-gray-900/90 backdrop-blur-xl',
      icon: 'text-white'
    };
  };
  const value: ThemeContextType = {
    theme,
    setTheme,
    language,
    setLanguage,
    t: translations[language],
    background,
    setBackground,
    reduceMotion,
    setReduceMotion,
    highContrast,
    setHighContrast,
    getThemeClasses
  };
  return <ThemeContext.Provider value={value} data-id="element-1018">{children}</ThemeContext.Provider>;
}
export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}